import Avatar from './Avatar';
import { flagEmoji, supportsFlagEmoji } from '../../lib/countries';
import { useLabels } from '../../i18n/labels';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface AthleteAvatarProps {
  firstName: string;
  lastName: string;
  photoUrl?: string | null;
  size?: AvatarSize;
  /** The ISO-2 country of the athlete. With no country, there is no flag. */
  country?: string | null;
  className?: string;
}

/** The flag grows with the avatar. The 32 px avatar of the Boxers tab has an 11 px flag (#518). */
const FLAG_SIZE: Record<AvatarSize, string> = {
  xs: 'text-[9px]',
  sm: 'text-[11px]',
  md: 'text-[13px]',
  lg: 'text-base',
  xl: 'text-xl',
};

/** #527 — the pill of the country code grows with the avatar too. */
const PILL_SIZE: Record<AvatarSize, string> = {
  xs: 'h-3 min-w-3.5 px-0.5 text-[7px] ring-1',
  sm: 'h-3.5 min-w-4 px-0.5 text-[9px] ring-2',
  md: 'h-4 min-w-5 px-1 text-[10px] ring-2',
  lg: 'h-5 min-w-7 px-1.5 text-xs ring-2',
  xl: 'h-6 min-w-8 px-2 text-sm ring-2',
};

/**
 * The avatar of an athlete, with the country flag at the bottom center (#522).
 * Each list of athletes uses this component, so the flag has one position:
 * the Boxers tab, the Athletes page and the Teams page.
 *
 * A browser that draws flag emoji shows the emoji, with no background (the
 * operator decision of #518). Chrome and Edge on Windows draw no flag emoji,
 * only the two letters: there the avatar shows the country code in the gray
 * pill that #518 had first (#527, `supportsFlagEmoji()`).
 */
export default function AthleteAvatar({ firstName, lastName, photoUrl, size = 'sm', country, className = '' }: AthleteAvatarProps) {
  const { countryName } = useLabels();
  const flag = country ? flagEmoji(country) : '';
  const emoji = supportsFlagEmoji();
  // The name of the country is the tooltip and the accessible name. An unknown code shows the code.
  const name = country ? countryName(country) : '';
  return (
    <span data-testid="athlete-avatar" className={`relative inline-flex shrink-0 ${className}`}>
      <Avatar firstName={firstName} lastName={lastName} photoUrl={photoUrl} size={size} />
      {flag && (
        <span
          data-testid="athlete-flag"
          data-flag={emoji ? 'emoji' : 'code'}
          role="img"
          title={name}
          aria-label={name}
          className={`absolute left-1/2 -bottom-1 -translate-x-1/2 leading-none ${emoji
            ? FLAG_SIZE[size]
            : `inline-flex items-center justify-center rounded-full bg-gray-200 ring-white font-semibold text-gray-700 ${PILL_SIZE[size]}`}`}
        >
          {emoji ? flag : country?.toUpperCase()}
        </span>
      )}
    </span>
  );
}
